import { Box, Button, Flex, VStack, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { FieldError, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import * as yup from 'yup'
import { Select } from '../../../../components'
import { api } from '../../../../services/api'
import { Modal } from './index'

const finishOrderSchema = yup.object().shape({
  payment_method_id: yup.string().required('Informe a forma de pagamento'),
  payment_condition_id: yup.string().required('Informe a condição de pagamento'),
})

interface FinishOrderModalProps {
  isOpen: boolean
  onClose: () => void
  serviceOrderId: number
  demandId: number
}

export function FinishOrderModal({ isOpen, onClose, serviceOrderId, demandId }: FinishOrderModalProps) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const { register, handleSubmit, formState } = useForm({
    resolver: yupResolver(finishOrderSchema),
  })

  const { data: paymentMethods } = useQuery(
    'paymentMethods',
    async () => {
      const response = await api.get('/payment-methods')
      return response.data?.data?.data || []
    },
    {
      retry: false,
    },
  )

  const { data: paymentConditions } = useQuery(
    'paymentConditions',
    async () => {
      const response = await api.get('/payment-conditions')
      return response.data?.data?.data || []
    },
    {
      retry: false,
    },
  )

  const finishOrder = useMutation(
    async (data: any) => {
      const response = await api.put(`/demands/${demandId}/service-orders/${serviceOrderId}`, {
        status: 'ENTREGUE',
        payment_method_id: data.payment_method_id,
        payment_condition_id: data.payment_condition_id,
        payday: new Date().toISOString().split('T')[0], // Data atual
      })
      return response.data
    },
    {
      onSuccess: () => {
        toast({
          description: 'Ordem de serviço finalizada com sucesso!',
          status: 'success',
        })
        queryClient.invalidateQueries('serviceOrderProduct')
        onClose()
      },
      onError: ({ response }) => {
        const errorList = response?.data?.errors || []
        if (Array.isArray(errorList) && errorList.length > 0) {
          errorList.forEach((err) => {
            toast({
              description: err.message || 'Erro ao finalizar ordem de serviço',
              status: 'error',
            })
          })
        } else {
          toast({
            description: 'Erro ao finalizar ordem de serviço',
            status: 'error',
          })
        }
      },
    },
  )

  const handleFinishOrder: SubmitHandler<any> = async (values) => {
    await finishOrder.mutateAsync(values)
  }

  const paymentMethodOptions = Array.isArray(paymentMethods)
    ? paymentMethods.map((method) => ({
        value: method.id,
        label: method.name,
      }))
    : []

  const paymentConditionOptions = Array.isArray(paymentConditions)
    ? paymentConditions.map((condition) => ({
        value: condition.id,
        label: condition.name,
      }))
    : []

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Finalizar Ordem de Serviço" size="md" hasFooter={false}>
      <Box as="form" onSubmit={handleSubmit(handleFinishOrder)}>
        <VStack spacing={6}>
          <Select
            label="Forma de Pagamento"
            {...register('payment_method_id')}
            options={paymentMethodOptions}
            placeholder="Selecione a forma de pagamento"
            error={formState.errors.payment_method_id as FieldError}
          />

          <Select
            label="Condição de Pagamento"
            {...register('payment_condition_id')}
            options={paymentConditionOptions}
            placeholder="Selecione a condição de pagamento"
            error={formState.errors.payment_condition_id as FieldError}
          />
        </VStack>

        <Flex mt={8} justify="flex-end" as="div">
          <Button type="submit" colorScheme="pink" isLoading={finishOrder.isLoading} loadingText="Finalizando...">
            Finalizar Ordem
          </Button>
        </Flex>
      </Box>
    </Modal>
  )
}
